import { HUBSPOT_INLINE_EMBED_SELECTOR, HUBSPOT_SCRIPT_ID, HUBSPOT_SCRIPT_SRC } from "./config";

type HubSpotWidget = {
  load: (options?: { widgetOpen?: boolean }) => void;
  open: () => void;
  close: () => void;
  remove: () => void;
  refresh: () => void;
};

type HubSpotConversations = {
  widget: HubSpotWidget;
  on?: (event: string, handler: (...args: Array<unknown>) => void) => void;
  off?: (event: string, handler: (...args: Array<unknown>) => void) => void;
};

type HubSpotConversationsSettings = {
  loadImmediately?: boolean;
  inlineEmbedSelector?: string;
};

declare global {
  interface Window {
    HubSpotConversations?: HubSpotConversations;
    hsConversationsSettings?: HubSpotConversationsSettings;
    hsConversationsOnReady?: Array<() => void>;
  }
}

let scriptPromise: Promise<HubSpotConversations> | null = null;
let widgetConfigured = false;

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function configureDeferred(): void {
  if (!isBrowser()) return;
  window.hsConversationsSettings = {
    loadImmediately: false,
    inlineEmbedSelector: HUBSPOT_INLINE_EMBED_SELECTOR,
  };
}

function whenReady(): Promise<HubSpotConversations> {
  return new Promise((resolve) => {
    if (window.HubSpotConversations) {
      resolve(window.HubSpotConversations);
      return;
    }
    if (!window.hsConversationsOnReady) {
      window.hsConversationsOnReady = [];
    }
    window.hsConversationsOnReady.push(() => {
      if (window.HubSpotConversations) {
        resolve(window.HubSpotConversations);
      }
    });
  });
}

export function loadScript(): Promise<HubSpotConversations> {
  if (!isBrowser()) {
    return Promise.reject(new Error("HubSpot support chat is not available outside the browser"));
  }
  if (scriptPromise) return scriptPromise;

  // Settings MUST be set before the script tag is added.
  configureDeferred();

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(HUBSPOT_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");
    if (!existing) {
      script.id = HUBSPOT_SCRIPT_ID;
      script.src = HUBSPOT_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
    }

    const handleLoad = () => {
      whenReady().then(resolve);
    };
    const handleError = () => {
      // Clear the cache so a retry can re-attempt.
      scriptPromise = null;
      reject(new Error(`Failed to load HubSpot support chat script: ${HUBSPOT_SCRIPT_SRC}`));
    };

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });

    if (!existing) {
      document.head.appendChild(script);
    }
  });

  return scriptPromise;
}

const WIDGET_RENDERED_TIMEOUT_MS = 4000;

/**
 * Resolves when HubSpot fires `widgetRendered` (iframe painted into the inline
 * embed) or after a safety-net timeout, whichever comes first. Avoids the
 * perceived gap between `widget.open()` returning and the iframe becoming
 * visible.
 */
function waitForWidgetRendered(conversations: HubSpotConversations): Promise<void> {
  return new Promise((resolve) => {
    if (!conversations.on || !conversations.off) {
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      conversations.off?.("widgetRendered", handler);
      clearTimeout(timeoutId);
      resolve();
    };
    const handler = () => finish();

    conversations.on("widgetRendered", handler);
    const timeoutId = setTimeout(finish, WIDGET_RENDERED_TIMEOUT_MS);
  });
}

export async function open(): Promise<void> {
  const conversations = await loadScript();

  const rendered = waitForWidgetRendered(conversations);

  if (widgetConfigured) {
    conversations.widget.refresh();
  } else {
    conversations.widget.load({ widgetOpen: true });
    widgetConfigured = true;
  }
  conversations.widget.open();

  await rendered;
}

export function close(): void {
  if (!isBrowser()) return;
  const conversations = window.HubSpotConversations;
  if (!conversations) return;
  conversations.widget.close();
  conversations.widget.remove();
  widgetConfigured = false;
}

/** Internal: reset module state between tests. Not re-exported from the barrel. */
export function __resetForTests(): void {
  scriptPromise = null;
  widgetConfigured = false;
  if (isBrowser()) {
    document.getElementById(HUBSPOT_SCRIPT_ID)?.remove();
    delete window.HubSpotConversations;
    delete window.hsConversationsSettings;
    delete window.hsConversationsOnReady;
  }
}
